import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useProfileContext } from "contexts/ProfileContext";
import { useVideoCallContext } from "contexts/VideoCallContext";
import { Spinner } from "shared/atoms/Spinner";
import { View } from "shared/containers/View";
import { CurrentPatientContext } from "utils/contexts";
import { useIsInRoute, useWindowSize } from "utils/hooks";
import { useActivityLogging } from "utils/hooks/useActivityLogging";
import { isTestRunning } from "utils/misc/isTestRunning";
import { isUsingActivityTracking } from "utils/patient";
import { userIsTherapist } from "utils/profile/profileHelper";
import { getStorageValue, removeStorageValue, setStorageValue } from "utils/storage";

import { PatientHeader } from "../PatientProfile/components/PatientHeader";
import { NewUserCourseScreen } from "../PatientProfile/components/UserCourse/NewUserCourseScreen";
import { UserCourseScreen } from "../PatientProfile/components/UserCourse/UserCourseScreen";
import { ActivityLog, Comments, Overview, Protocol } from "../PatientProfile/views";
import { Information } from "../PatientProfile/views/Information";
import { acknowledgeNewPatientAlert } from "../queries/acknowledgeNewPatientAlert";

import { ConversationSidePanel } from "./components/ConversationSidePanel";
import { ConversationToggleButton } from "./components/ConversationToggleButton";
import { Treatment } from "./Treatment";
import { DRAWER_WIDTH, TemplateSelectionModal } from "./Treatment/components/TreatmentEditor/TemplateSelectionModal";

interface LocationState {
  careEventID: number;
  pathname: string;
  text: string;
}

export const PatientProfile: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const state = useLocation().state as LocationState;
  const { profile } = useProfileContext();
  const {
    patient,
    patientLoading,
    setActiveCareEventNote,
    setPatientId,
    setPreviousPage,
    previousTreatmentId,
    setPreviousTreatmentId,
    showSelectTreatmentSidebar,
    setShowSelectTreatmentSidebar,
  } = useContext(CurrentPatientContext);
  const { height } = useWindowSize();
  const { patientId } = useParams();

  invariant(profile, "Profile should be defined");
  const isTherapist = userIsTherapist(profile);

  const { callData, videoCallMode } = useVideoCallContext();
  const inMaximisedCall = callData !== null && videoCallMode === "docked";
  const [sideContainer, setSideContainer] = useState(!getStorageValue("hideSideContainer"));
  const { trackActivity } = useActivityLogging();
  const startTime = useRef(new Date().toISOString());

  const isInTreatmentView = useIsInRoute(["treatment"]);
  const hideSidebar = isInTreatmentView;
  const isInUserCourse = useIsInRoute(["usercourse"]);

  const patientName = patient ? `${patient.first_name} ${patient.last_name}` : "";
  const additionalBreadcrumbs = [{ label: patientName, href: "" }];
  const isSidePanelOpen = isTherapist && sideContainer && !hideSidebar && !showSelectTreatmentSidebar;

  useEffect(() => {
    setShowSelectTreatmentSidebar(false);
  }, []);

  useEffect(() => {
    if (state?.careEventID) {
      setActiveCareEventNote(state.careEventID);
    }
    if (state) {
      setPreviousPage(state);
    }
  }, [state]);

  useEffect(() => {
    setPatientId(patientId);

    return () => {
      if (isTherapist && patientId && patient && isUsingActivityTracking(patient) && !isTestRunning()) {
        trackActivity({
          category: "patient_profile_viewed",
          patient_id: parseInt(patientId, 10),
          start_time: startTime.current,
          end_time: new Date().toISOString(),
        });
      }
      setPatientId(undefined);
      setPreviousTreatmentId(null);
      setActiveCareEventNote(null);
    };
  }, [patientId]);

  useEffect(() => {
    if (isTherapist && profile && patient?.new_patient) {
      acknowledgeNewPatientAlert(String(patient.id), String(profile.id)).catch((error: unknown) => {
        /* eslint-disable no-console */
        console.warn("acknowledgeNewPatientAlert error", error);
      });
    }
  }, [isTherapist, profile, patient]);

  const toggleSideContainer = () => {
    if (sideContainer) {
      setSideContainer(false);
      setStorageValue("hideSideContainer", "hide");
    } else {
      setSideContainer(true);
      removeStorageValue("hideSideContainer");
    }
  };

  const templateModal =
    isTherapist && !isInTreatmentView && showSelectTreatmentSidebar ? (
      <TemplateSelectionModal
        awaitingTreatmentReady={patient?.awaiting_treatment_ready}
        patientId={patient?.id}
        onSelect={templateID => {
          navigate(`/patients/${patient?.id}/treatment/new${templateID ? `/${templateID}` : ""}`);
          setShowSelectTreatmentSidebar(false);
        }}
        onClose={() => setShowSelectTreatmentSidebar(false)}
      />
    ) : null;

  if (!patient || patientLoading) {
    return (
      <LoadingContainer $vh={height}>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isInTreatmentView) {
    return (
      <Wrapper>
        <Container $showSelectTreatmentSidebar={showSelectTreatmentSidebar}>
          <Routes>
            <Route
              path="treatment/edit"
              element={<Treatment editorSettings={{ context: "patient", mode: "edit" }} />}
            />
            <Route
              path="treatment/new/:templateID"
              element={<Treatment editorSettings={{ context: "patient", mode: "build" }} />}
            />
            <Route
              path="treatment/new"
              element={<Treatment editorSettings={{ context: "patient", mode: "build" }} />}
            />
          </Routes>
        </Container>
        {templateModal}
      </Wrapper>
    );
  }

  if (isInUserCourse) {
    return (
      <Wrapper>
        <Container $showSelectTreatmentSidebar={showSelectTreatmentSidebar}>
          <SubMenuContainerAlt $vh={height} $isSidePanelOpen={isSidePanelOpen} $inMaximisedCall={inMaximisedCall}>
            <View additionalBreadcrumbs={additionalBreadcrumbs}>
              <PatientsContentContainer $adjustForCall={inMaximisedCall}>
                <Routes>
                  <Route path="usercourse" element={<UserCourseScreen />} />
                  <Route path="usercourse/new" element={<NewUserCourseScreen />} />
                </Routes>
              </PatientsContentContainer>
            </View>
            {isTherapist && (
              <ConversationToggleButton
                isOpen={isSidePanelOpen}
                inMaximisedCall={inMaximisedCall}
                onClick={toggleSideContainer}
              />
            )}
          </SubMenuContainerAlt>
        </Container>
        {isTherapist && (
          <ConversationSidePanel isOpen={isSidePanelOpen} patientId={patient.id} onClose={toggleSideContainer} />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Container $showSelectTreatmentSidebar={showSelectTreatmentSidebar}>
        <SubMenuContainer
          $vh={height}
          $isSidePanelOpen={isSidePanelOpen}
          $inMaximisedCall={inMaximisedCall}
          id="comments-container"
        >
          <View additionalBreadcrumbs={additionalBreadcrumbs}>
            <PatientHeader />
            <PatientsContentContainer $adjustForCall={inMaximisedCall}>
              {previousTreatmentId && (
                <PreviousTreatmentNotification>
                  {t("patients.previous_treatment_selected")}
                </PreviousTreatmentNotification>
              )}
              <Routes>
                <Route path="overview" element={<Overview />} />
                <Route path="comments" element={<Comments />} />
                <Route path="protocol" element={<Navigate to="current" state={state} />} />
                <Route path="protocol/:weekInfo/*" element={<Protocol />} />
                <Route path="information/*" element={<Information />} />
                <Route path="activity_log" element={<ActivityLog />} />
                <Route path="/" element={<Navigate to="overview" replace state={state} />} />
              </Routes>
            </PatientsContentContainer>
          </View>
          {isTherapist && (
            <ConversationToggleButton
              isOpen={isSidePanelOpen}
              inMaximisedCall={inMaximisedCall}
              onClick={toggleSideContainer}
            />
          )}
        </SubMenuContainer>
      </Container>

      {isTherapist && (
        <ConversationSidePanel isOpen={isSidePanelOpen} patientId={patient.id} onClose={toggleSideContainer} />
      )}
      {templateModal}
    </Wrapper>
  );
};

const SubMenuContainer = styled.div<{ $vh: number; $isSidePanelOpen: boolean; $inMaximisedCall: boolean }>`
  height: ${({ $vh }) => ($vh ? `${$vh}px` : "100vh")};
  overflow-y: auto;
  overflow-x: hidden;
  flex-grow: 1;
  contain: inline-size;
  position: relative;
  margin-right: ${({ $isSidePanelOpen, $inMaximisedCall, theme }) => {
    if ($isSidePanelOpen) return "400px";
    if ($inMaximisedCall) return `${theme.videoCallWidthMaximised}px`;
    return "0";
  }};
  transition: ${({ $isSidePanelOpen }) =>
    $isSidePanelOpen
      ? "margin-right 225ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
      : "margin-right 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms"};
`;

const SubMenuContainerAlt = styled(SubMenuContainer)`
  background-color: ${({ theme }) => theme.colors.greys.light2};
`;

const PatientsContentContainer = styled.div<{ $adjustForCall: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0 0 12px 12px;
`;

const LoadingContainer = styled.div<{ $vh: number }>`
  height: ${({ $vh }) => ($vh ? `${$vh}px` : "100vh")};
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled.div<{ $showSelectTreatmentSidebar: boolean }>`
  flex-grow: 1;
  display: flex;
  margin-right: ${({ $showSelectTreatmentSidebar }) => ($showSelectTreatmentSidebar ? `${DRAWER_WIDTH}px` : "0")};
  transition: margin-right 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;
`;

const PreviousTreatmentNotification = styled.div`
  color: ${({ theme }) => theme.colors.primary.base};
  background-color: ${({ theme }) => theme.colors.exercise.background};
  border: 1px solid ${({ theme }) => theme.colors.exercise.accent};
  max-width: 1000px;
  box-sizing: border-box;
  margin: 24px auto 12px;
  padding: 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  text-align: center;
  ${({ theme }) => theme.belowBreakpoint} {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;
